// Team Trends (Identity/Billing roadmap, Phase 3). The manager view: every rep's OVERALL final-score
// trend across their last ~24 months of scored calls. Scores only (no transcripts, no coaching) —
// data comes from /api/team-trends (gated on 'team_history'; the nav hides it from members). Fails
// quiet: a 403/error or trends-off shows an explanatory empty state, never a broken page.

import * as api from '../lib/api.js';
import { openTrendsForRep } from './trends.js';

function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

const fmt = (v: number | null): string => (v === null ? '—' : v.toFixed(1));
const fmtDate = (ms: number | null): string => {
  if (!ms) return '—';
  try {
    return new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

/** Fetch + paint the team trends table into #team-trends-app. Called each time the view is shown. */
export function renderTeamTrends(): void {
  const root = document.getElementById('team-trends-app');
  if (!root) return;
  root.textContent = '';
  root.appendChild(h('p', 'tt-loading', 'Loading team trends…'));
  Promise.all([
    api.getTeamTrends(showRemoved),
    api.listTeams().catch(() => ({ teams: [] as api.Team[] })),
  ])
    .then(([res, t]) => paint(root, res, t.teams))
    .catch(() => {
      root.textContent = '';
      root.appendChild(empty('Team trends are unavailable right now. Please try again.'));
    });
}

function empty(msg: string): HTMLElement {
  return h('p', 'tt-empty', msg);
}

// Sort state (Phase 1). Null key = the backend's default order (most active first). Clicking a sortable
// header toggles asc/desc; names default A→Z, numbers default high→low (most useful first look).
type SortKey = 'name' | 'team' | 'region' | 'manager' | 'class' | 'calls' | 'latest' | 'avg' | 'lastTs' | 'trend';
const STRING_SORT_KEYS = new Set<SortKey>(['name', 'team', 'region', 'manager', 'class']);
let currentReps: api.TeamRepTrend[] = [];
// One multi-select filter per org entity. Empty set = no filter on that entity. Members are the
// entity's ids or the literal 'none' (reps unassigned on it). Filters AND across entities; a rep
// shows only if it matches every non-empty filter.
const teamFilter = new Set<string>();
const regionFilter = new Set<string>();
const managerFilter = new Set<string>();
const classFilter = new Set<string>();
// Free-text rep search (name substring, case-insensitive). Empty = no filter.
let searchTerm = '';
let sortState: { key: SortKey | null; dir: 'asc' | 'desc' } = { key: null, dir: 'asc' };
// Selection + Remove (Phase 3).
const selected = new Set<string>();
let showRemoved = false;
let removeBtn: HTMLButtonElement | null = null;
let restoreBtn: HTMLButtonElement | null = null;

/** Enable Remove/Restore from the selection (Remove acts on visible reps, Restore on removed ones). */
function syncToolbar(): void {
  if (removeBtn) removeBtn.disabled = !currentReps.some((r) => selected.has(r.repKey) && !r.hidden);
  if (restoreBtn) restoreBtn.disabled = !currentReps.some((r) => selected.has(r.repKey) && r.hidden);
}

function hideAction(hidden: boolean): void {
  const keys = currentReps.filter((r) => selected.has(r.repKey) && r.hidden !== hidden).map((r) => r.repKey);
  if (!keys.length) return;
  if (hidden && !confirm(`Remove ${keys.length} rep${keys.length === 1 ? '' : 's'} from Average Scores? Their data is kept for 24 months; use “Show removed” to restore.`)) return;
  selected.clear();
  api.hideReps(keys, hidden).then(() => renderTeamTrends()).catch(() => renderTeamTrends());
}

function paint(
  root: HTMLElement,
  res: { reps: api.TeamRepTrend[]; trendsOff?: boolean },
  teams: api.Team[],
): void {
  root.textContent = '';
  document.getElementById('team-trends-rail')?.replaceChildren(); // empty/off states carry no filters
  if (res.trendsOff) {
    root.appendChild(empty('Trends are off, so no team history is being built. Turn on “Enable Trends” in Account to start collecting it.'));
    return;
  }
  if (!res.reps.length) {
    root.appendChild(empty('No scored calls yet. As your reps’ calls are scored, each rep’s 24-month trend appears here.'));
    return;
  }

  currentReps = res.reps;
  teamFilter.clear();
  regionFilter.clear();
  managerFilter.clear();
  classFilter.clear();
  searchTerm = '';
  sortState = { key: null, dir: 'asc' }; // fresh load → backend order
  selected.clear();

  // Build the search / action / filter controls (wiring unchanged) — they live in the right RAIL now,
  // leaving the wide table the full left card. The classes stay the same so the dark skin applies.
  const search = document.createElement('input');
  search.type = 'search';
  search.placeholder = 'Search reps…';
  search.className = 'tt-search';
  search.value = searchTerm;
  search.setAttribute('aria-label', 'Search reps by name');
  search.addEventListener('input', () => {
    searchTerm = search.value;
    const m = document.getElementById('tt-table-mount');
    if (m) renderTable(m);
  });
  removeBtn = h('button', 'tt-tool-btn', 'Remove');
  removeBtn.type = 'button';
  removeBtn.addEventListener('click', () => hideAction(true));
  restoreBtn = null;
  if (showRemoved) {
    restoreBtn = h('button', 'tt-tool-btn', 'Restore');
    restoreBtn.type = 'button';
    restoreBtn.addEventListener('click', () => hideAction(false));
  }
  const showLbl = h('label', 'tt-show-removed');
  const showCb = document.createElement('input');
  showCb.type = 'checkbox';
  showCb.checked = showRemoved;
  showCb.addEventListener('change', () => {
    showRemoved = showCb.checked;
    renderTeamTrends();
  });
  showLbl.appendChild(showCb);
  showLbl.appendChild(h('span', undefined, 'Show removed'));

  // Left card body: a rep count, then the scrolling table.
  root.appendChild(h('p', 'tt-count', `${res.reps.length} rep${res.reps.length === 1 ? '' : 's'}`));
  const mount = h('div', 'tt-scroll');
  mount.id = 'tt-table-mount';
  root.appendChild(mount);
  renderTable(mount);

  // Right rail: a "Reps" card (search + actions) and a "Filters" card (the 4 org-entity multi-selects).
  const railRoot = document.getElementById('team-trends-rail');
  if (railRoot) {
    railRoot.textContent = '';
    const rail = h('div', 'trf-rail-inner');
    const c1 = h('div', 'tr-rail-card');
    c1.appendChild(h('div', 'tr-rail-h', 'Reps'));
    c1.appendChild(search);
    const actions = h('div', 'tt-rail-actions');
    actions.appendChild(removeBtn);
    if (restoreBtn) actions.appendChild(restoreBtn);
    c1.appendChild(actions);
    c1.appendChild(showLbl);
    rail.appendChild(c1);
    const c2 = h('div', 'tr-rail-card');
    c2.appendChild(h('div', 'tr-rail-h', 'Filters'));
    c2.appendChild(entityFilterMenu('Team', teamFilter, 'teamId', 'teamName', 'No team'));
    c2.appendChild(entityFilterMenu('Region', regionFilter, 'regionId', 'regionName', 'No region'));
    c2.appendChild(entityFilterMenu('Manager', managerFilter, 'managerId', 'managerName', 'No manager'));
    c2.appendChild(entityFilterMenu('Class', classFilter, 'classId', 'className', 'No class'));
    rail.appendChild(c2);
    railRoot.appendChild(rail);
  }
  syncToolbar();
}

function matchEntity(set: Set<string>, id: string | null): boolean {
  return set.size === 0 || set.has(id ?? 'none'); // empty filter = no constraint; 'none' = unassigned
}
function applyFilter(list: api.TeamRepTrend[]): api.TeamRepTrend[] {
  return list.filter(
    (r) =>
      matchEntity(teamFilter, r.teamId) &&
      matchEntity(regionFilter, r.regionId) &&
      matchEntity(managerFilter, r.managerId) &&
      matchEntity(classFilter, r.classId),
  );
}

/** Name search — case-insensitive substring on the rep's display name. */
function applySearch(list: api.TeamRepTrend[]): api.TeamRepTrend[] {
  const q = searchTerm.trim().toLowerCase();
  if (!q) return list;
  return list.filter((r) => r.name.toLowerCase().includes(q));
}

type EntityIdKey = 'teamId' | 'regionId' | 'managerId' | 'classId';
type EntityNameKey = 'teamName' | 'regionName' | 'managerName' | 'className';

/** Distinct {id, name, count} options for one entity, derived from the loaded reps, name-sorted, plus
 *  the count of unassigned reps (the "none" bucket). Only values present in the data become options. */
function entityOptions(idKey: EntityIdKey, nameKey: EntityNameKey): { opts: { id: string; name: string; count: number }[]; none: number } {
  const map = new Map<string, { name: string; count: number }>();
  let none = 0;
  for (const r of currentReps) {
    const id = r[idKey];
    if (id == null) { none += 1; continue; }
    const e = map.get(id) ?? { name: r[nameKey] ?? id, count: 0 };
    e.count += 1;
    map.set(id, e);
  }
  const opts = [...map.entries()]
    .map(([id, e]) => ({ id, name: e.name, count: e.count }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return { opts, none };
}

/** Multi-select filter for one org entity: a <details> menu of checkboxes (each value + the unassigned
 *  bucket). Selecting more than one shows reps matching ANY chosen value; no selection = no constraint. */
function entityFilterMenu(label: string, filterSet: Set<string>, idKey: EntityIdKey, nameKey: EntityNameKey, noneLabel: string): HTMLElement {
  const { opts, none } = entityOptions(idKey, nameKey);
  const box = document.createElement('details');
  box.className = 'tt-filter';
  const summary = document.createElement('summary');
  summary.className = 'tt-filter-summary';
  const caption = h('span', 'tt-filter-caption');
  summary.appendChild(h('span', 'tt-filter-label', label));
  summary.appendChild(caption);
  box.appendChild(summary);

  const menu = h('div', 'tt-filter-menu');
  const refresh = (): void => {
    if (filterSet.size === 0) caption.textContent = 'All';
    else if (filterSet.size === 1) {
      const only = [...filterSet][0]!;
      caption.textContent = only === 'none' ? noneLabel : (opts.find((o) => o.id === only)?.name ?? '1 selected');
    } else caption.textContent = `${filterSet.size} selected`;
    const mount = document.getElementById('tt-table-mount');
    if (mount) renderTable(mount);
  };

  const addRow = (key: string, text: string): void => {
    const row = h('label', 'tt-filter-opt');
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.className = 'tt-check';
    cb.checked = filterSet.has(key);
    cb.addEventListener('change', () => {
      if (cb.checked) filterSet.add(key);
      else filterSet.delete(key);
      refresh();
    });
    row.appendChild(cb);
    row.appendChild(h('span', undefined, text));
    menu.appendChild(row);
  };
  for (const o of opts) addRow(o.id, `${o.name} (${o.count})`);
  if (none > 0) addRow('none', `${noneLabel} (${none})`);

  const clear = h('button', 'tt-filter-clear', 'Clear');
  clear.type = 'button';
  clear.addEventListener('click', () => {
    filterSet.clear();
    for (const cb of Array.from(menu.querySelectorAll<HTMLInputElement>('input[type=checkbox]'))) cb.checked = false;
    refresh();
  });
  menu.appendChild(clear);

  box.appendChild(menu);
  refresh();
  return box;
}

function toggleSort(key: SortKey): void {
  if (sortState.key === key) sortState.dir = sortState.dir === 'asc' ? 'desc' : 'asc';
  else sortState = { key, dir: STRING_SORT_KEYS.has(key) ? 'asc' : 'desc' }; // text A→Z, numbers high→low first
  const mount = document.getElementById('tt-table-mount');
  if (mount) renderTable(mount);
}

/** The sortable value for a rep under one column key. Strings for the name/entity columns, numbers for
 *  the metric columns, null when there's nothing to sort on (unassigned entity, or a trend that needs
 *  ≥2 calls). */
function sortVal(r: api.TeamRepTrend, key: SortKey): string | number | null {
  switch (key) {
    case 'name': return r.name;
    case 'team': return r.teamName;
    case 'region': return r.regionName;
    case 'manager': return r.managerName;
    case 'class': return r.className;
    case 'calls': return r.callCount;
    case 'latest': return r.latest;
    case 'avg': return r.avg;
    case 'lastTs': return r.lastTs;
    case 'trend': return r.latest !== null && r.first !== null && r.callCount > 1 ? r.latest - r.first : null;
  }
}

/** Sort a copy of the reps by the active column; nulls/unassigned always sort last, both directions. */
function applySort(list: api.TeamRepTrend[]): api.TeamRepTrend[] {
  const key = sortState.key;
  if (!key) return list;
  const mul = sortState.dir === 'asc' ? 1 : -1;
  const isStr = STRING_SORT_KEYS.has(key);
  return [...list].sort((a, b) => {
    const av = sortVal(a, key);
    const bv = sortVal(b, key);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return isStr ? mul * String(av).localeCompare(String(bv)) : mul * ((av as number) - (bv as number));
  });
}

function sortableTh(label: string, key: SortKey): HTMLElement {
  const th = h('th', 'tt-th-sort');
  const btn = h('button', 'tt-sort-btn');
  btn.type = 'button';
  btn.appendChild(h('span', undefined, label));
  btn.appendChild(h('span', 'tt-sort-arrow', sortState.key === key ? (sortState.dir === 'asc' ? '▲' : '▼') : ''));
  btn.addEventListener('click', () => toggleSort(key));
  th.appendChild(btn);
  return th;
}

function renderTable(mount: HTMLElement): void {
  mount.textContent = '';
  const table = h('table', 'tt-table');
  const rows = applySort(applySearch(applyFilter(currentReps)));

  const thead = h('thead');
  const htr = h('tr');
  // Master select-all checkbox (selects the currently-visible rows).
  const masterTh = h('th', 'tt-check-cell');
  const master = document.createElement('input');
  master.type = 'checkbox';
  master.className = 'tt-check';
  master.checked = rows.length > 0 && rows.every((r) => selected.has(r.repKey));
  master.addEventListener('change', () => {
    for (const r of rows) {
      if (master.checked) selected.add(r.repKey);
      else selected.delete(r.repKey);
    }
    renderTable(mount);
    syncToolbar();
  });
  masterTh.appendChild(master);
  htr.appendChild(masterTh);
  htr.appendChild(sortableTh('Rep', 'name'));
  htr.appendChild(sortableTh('Team', 'team'));
  htr.appendChild(sortableTh('Region', 'region'));
  htr.appendChild(sortableTh('Manager', 'manager'));
  htr.appendChild(sortableTh('Class', 'class'));
  htr.appendChild(sortableTh('Calls', 'calls'));
  htr.appendChild(sortableTh('Latest', 'latest'));
  htr.appendChild(sortableTh('Average', 'avg'));
  htr.appendChild(sortableTh('Last scored', 'lastTs'));
  htr.appendChild(sortableTh('24-month trend', 'trend'));
  thead.appendChild(htr);
  table.appendChild(thead);

  const tbody = h('tbody');
  if (!rows.length) {
    const tr = h('tr');
    const td = h('td', 'tt-empty-row', searchTerm.trim() ? 'No reps match your search.' : showRemoved ? 'No reps to show.' : 'No reps in this team yet.');
    td.colSpan = 11;
    tr.appendChild(td);
    tbody.appendChild(tr);
  }
  for (const r of rows) {
    const tr = h('tr', r.hidden ? 'tt-row-hidden' : undefined);
    const checkTd = h('td', 'tt-check-cell');
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.className = 'tt-check';
    cb.checked = selected.has(r.repKey);
    cb.addEventListener('change', () => {
      if (cb.checked) selected.add(r.repKey);
      else selected.delete(r.repKey);
      master.checked = rows.length > 0 && rows.every((x) => selected.has(x.repKey));
      syncToolbar();
    });
    checkTd.appendChild(cb);
    tr.appendChild(checkTd);
    const who = h('td');
    const nameRow = h('div', 'tt-rep-name');
    // The name is a link into Trends, pre-filtered to this rep (Rep's-name filter inherits the name).
    const nameBtn = h('button', 'tt-rep-link');
    nameBtn.type = 'button';
    nameBtn.appendChild(document.createTextNode(r.name));
    nameBtn.title = `See ${r.name}'s trends`;
    nameBtn.addEventListener('click', () => openTrendsForRep(r.repKey));
    nameRow.appendChild(nameBtn);
    who.appendChild(nameRow);
    if (r.hidden) who.appendChild(h('div', 'tt-rep-hint', 'removed'));
    tr.appendChild(who);
    tr.appendChild(entityCell(r.teamName, 'No team'));
    tr.appendChild(entityCell(r.regionName, '—'));
    tr.appendChild(entityCell(r.managerName, '—'));
    tr.appendChild(entityCell(r.className, '—'));
    tr.appendChild(h('td', 'tt-num', String(r.callCount)));
    tr.appendChild(h('td', 'tt-num', fmt(r.latest)));
    tr.appendChild(h('td', 'tt-num tt-avg', fmt(r.avg))); // the headline metric — emphasized (see .tt-avg)
    tr.appendChild(h('td', undefined, fmtDate(r.lastTs)));

    // 24-month trend: direction + delta only (latest vs first over the window). The sparkline was
    // removed — it double-encoded the same signal and looked sad for single-call reps.
    const delta = r.latest !== null && r.first !== null ? r.latest - r.first : null;
    const dirCell = h('td', 'tt-trend-cell');
    if (delta !== null && r.callCount > 1) {
      const dir = delta > 0.15 ? 'up' : delta < -0.15 ? 'down' : 'flat';
      const label = dir === 'up' ? `▲ +${delta.toFixed(1)}` : dir === 'down' ? `▼ ${delta.toFixed(1)}` : 'steady';
      dirCell.appendChild(h('span', `tt-delta tt-delta--${dir}`, label));
    } else {
      dirCell.appendChild(h('span', 'tt-delta tt-delta--flat', 'new'));
    }
    tr.appendChild(dirCell);

    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  mount.appendChild(table);
}

/** The Team cell: read-only. Reps are assigned on the dedicated Teams page (under The CRO Zone);
 *  here it just shows which team a rep belongs to. */
/** One org-entity cell (team/region/manager/class): the value, or a muted placeholder when unassigned. */
function entityCell(name: string | null, emptyLabel: string): HTMLElement {
  const td = h('td', 'tt-team-cell');
  td.appendChild(name ? h('span', 'tt-team-name', name) : h('span', 'tt-team-none', emptyLabel));
  return td;
}

